/**
 * TUS Resumable Upload Utility
 * 
 * Implements resumable uploads using the TUS protocol for large files.
 * Supabase Storage natively supports TUS, providing:
 * - Real progress tracking
 * - Resumable uploads after network interruption
 * - Chunked uploads (6MB chunks)
 * - Automatic retries with exponential backoff
 */

import * as tus from 'tus-js-client';
import { TUS_ENDPOINT, TUS_CHUNK_SIZE, TUS_RETRY_DELAYS } from './constants';
import type { UploadProgress, UploadController } from './types';

export interface TusUploadOptions {
  /** File to upload */
  file: File;
  /** Storage bucket name */
  bucketName: string;
  /** Object path within the bucket */
  objectPath: string;
  /** Callback for progress updates */
  onProgress: (progress: UploadProgress) => void;
  /** Callback when upload fails */
  onError: (error: Error) => void;
  /** Callback when upload completes */
  onSuccess: () => void;
  /** Callback when upload is aborted by user */
  onAbort?: () => void;
  /** Supabase access token for authentication */
  accessToken: string;
}

/**
 * Calculates upload speed using rolling average
 */
class SpeedCalculator {
  private samples: { bytes: number; timestamp: number }[] = [];
  private maxSamples = 5;

  addSample(bytesUploaded: number): number {
    const now = Date.now();
    this.samples.push({ bytes: bytesUploaded, timestamp: now });
    
    // Keep only recent samples
    if (this.samples.length > this.maxSamples) {
      this.samples.shift();
    }
    
    if (this.samples.length < 2) return 0;
    
    const first = this.samples[0];
    const last = this.samples[this.samples.length - 1];
    const bytesDiff = last.bytes - first.bytes;
    const timeDiff = (last.timestamp - first.timestamp) / 1000; // seconds
    
    return timeDiff > 0 ? bytesDiff / timeDiff : 0;
  }

  reset(): void {
    this.samples = [];
  }
}

/**
 * Creates a TUS resumable upload
 * 
 * @param options - Upload configuration
 * @returns Controller to manage the upload
 * 
 * @example
 * ```typescript
 * const controller = createTusUpload({
 *   file: largeFile,
 *   bucketName: 'documents',
 *   objectPath: 'jobs/123/file.pdf',
 *   accessToken: session.access_token,
 *   onProgress: (progress) => setProgress(progress),
 *   onError: (err) => toast.error(err.message),
 *   onSuccess: () => toast.success('Upload complete'),
 * });
 * 
 * controller.start();
 * // Later: controller.pause(), controller.resume(), controller.abort()
 * ```
 */
export function createTusUpload(options: TusUploadOptions): UploadController {
  const { file, bucketName, objectPath, onProgress, onError, onSuccess, onAbort, accessToken } = options;
  const speedCalculator = new SpeedCalculator();
  let upload: tus.Upload | null = null;
  let isPaused = false;
  let isAborted = false;

  const createUpload = () => {
    upload = new tus.Upload(file, {
      endpoint: TUS_ENDPOINT,
      retryDelays: TUS_RETRY_DELAYS,
      chunkSize: TUS_CHUNK_SIZE,
      headers: {
        authorization: `Bearer ${accessToken}`,
        'x-upsert': 'true', // Overwrite if exists
      },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      metadata: {
        bucketName: bucketName,
        objectName: objectPath,
        contentType: file.type,
        cacheControl: '3600',
      },
      onError: (err) => {
        // Don't report error if paused or aborted intentionally
        if (isPaused || isAborted) return;
        console.error('TUS upload error:', err);
        onError(new Error(err.message || 'Upload failed'));
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        const percentage = Math.round((bytesUploaded / bytesTotal) * 100);
        const uploadSpeed = speedCalculator.addSample(bytesUploaded);
        const bytesRemaining = bytesTotal - bytesUploaded;
        const estimatedTimeRemaining = uploadSpeed > 0 
          ? Math.round(bytesRemaining / uploadSpeed) 
          : undefined;

        onProgress({
          bytesUploaded,
          bytesTotal,
          percentage,
          uploadSpeed: uploadSpeed > 0 ? uploadSpeed : undefined,
          estimatedTimeRemaining,
        });
      },
      onSuccess: () => {
        speedCalculator.reset();
        onSuccess();
      },
    });
  };

  return {
    start: () => {
      isPaused = false;
      isAborted = false;
      createUpload();
      upload?.start();
    },
    pause: () => {
      isPaused = true;
      upload?.abort();
    },
    resume: () => {
      isPaused = false;
      if (upload) {
        upload.start();
      } else {
        createUpload();
        upload?.start();
      }
    },
    abort: () => {
      isAborted = true;
      upload?.abort();
      upload = null;
      speedCalculator.reset();
      onAbort?.();
    },
  };
}
